import {
  MutationCache,
  QueryCache,
  QueryClient,
  type DefaultOptions,
} from '@tanstack/react-query';
import { classifyApiError, type ApiErrorKind } from './classifyApiError';
import {
  getErrorToastContent,
  type ErrorToastContent,
} from './getErrorToastContent';
import { wasErrorHandledLocally } from './locallyHandledErrors';
// The `Register` module augmentation that types `meta.suppressErrorToast` is
// defined in `./queryMeta` and re-exported from the lib's barrel
// (`src/index.ts`), so any consumer importing from `@ais-ui/custom-hooks`
// picks it up transitively — no local side-effect import needed here.

/**
 * Default window during which a repeat *generic* error (403/5xx/network, and
 * 401 globally) for the same endpoint is suppressed. Set to 5 minutes so a
 * backend going down mid-session doesn't spam the operator with an identical
 * toast on every polling attempt or every request to the same endpoint.
 * Non-generic errors (validation/input problems) are not throttled at all —
 * the user has to react to each one.
 */
const DEFAULT_TOAST_THROTTLE_MS = 5 * 60 * 1000;

/**
 * Upper bound on tracked throttle entries. Each distinct endpoint + error kind
 * claims a slot, so without pruning the map could grow for the lifetime of a
 * long-lived session. When the cap is hit, expired entries are swept; if
 * everything is still fresh, the oldest entries are evicted — the worst case
 * is a repeat toast, never a leak.
 */
const MAX_THROTTLE_ENTRIES = 100;

/**
 * TEMPORARY kill-switch: the backend currently returns spurious 401s at the
 * start of a session, which would surface a misleading "log in again" toast.
 * While `true`, 401 (`unauthorized`) errors never toast. Flip to `false` once
 * the backend is fixed to restore the globally-throttled 401 toast.
 */
const SUPPRESS_UNAUTHORIZED_TOASTS = true;

/**
 * Error kinds treated as "generic" backend/infrastructure failures — the user
 * can't fix them by changing their input (a 422 validation error is the
 * opposite: it's actionable and must be surfaced every time). These throttle
 * to at most one toast per endpoint per `toastThrottleMs`. `unauthorized`
 * (401) is intentionally *not* in this set: a single session expiry typically
 * fails many different endpoints at once, so it is throttled globally instead
 * (one toast app-wide rather than one per endpoint).
 */
const GENERIC_ERROR_KINDS: ReadonlySet<ApiErrorKind> = new Set([
  'forbidden', // 403
  'server', // 5xx
  'network', // no HTTP response — backend unreachable
]);

/**
 * Derives the logical endpoint for a query/mutation from its key array. By
 * convention the first element is the operation/resource name (e.g.
 * `['shipInfo', mmsi]`); everything after it is dynamic parameters. Returns
 * `undefined` for keyless mutations, which are not throttled because they are
 * user-initiated.
 */
function endpointFromKey(
  key: readonly unknown[] | undefined,
): string | undefined {
  const first = key?.[0];
  if (first === undefined) return undefined;
  return typeof first === 'string' ? first : JSON.stringify(first);
}

export interface CreateAppQueryClientOptions {
  /**
   * Delivers the error toast — pass `toast` from `@ais-ui/ui-core`.
   *
   * Injected rather than imported so this lib stays free of a `ui-core`
   * dependency: `ui-core` already imports `custom-hooks`
   * (`InfiniteScroll.tsx`), so importing `ui-core` here would create a
   * library-level dependency cycle.
   */
  notify: (content: ErrorToastContent) => void;
  /**
   * Merged into the client's `defaultOptions` (layered on top of this
   * factory's own retry/error-handling defaults). Use this for app-specific
   * `staleTime`/`gcTime` presets — see `apps/ais/src/main.tsx` for an example
   * that preserves its existing timing behavior unchanged.
   */
  defaultOptions?: DefaultOptions;
  /** Override the default 5-minute generic-error toast throttle window (ms). */
  toastThrottleMs?: number;
}

/**
 * Client errors (4xx) are deterministic — retrying the exact same request
 * again won't change the outcome, so don't waste attempts before surfacing
 * the error to the user.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const { status } = classifyApiError(error);
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < 3;
}

/**
 * Builds a `QueryClient` wired so that **every** failed query and mutation in
 * the app surfaces a single, consistent toast to the user.
 *
 * - Queries: always toast unless `meta: { suppressErrorToast: true }`.
 * - Mutations: toast unless `meta: { suppressErrorToast: true }`, the
 *   mutation defines `onError` in its `useMutation` options, or a local
 *   `mutate(variables, { onError })` callback marked the error via
 *   `markErrorHandledLocally` (the `mutateCallbacks` family and other
 *   toast-showing handlers do this) — so existing wired-up mutations keep
 *   working unchanged with no double-toasting.
 * - Errors classified as `service-unavailable` never toast: the
 *   circuit-breaker/service-availability UX already owns those.
 * - A 401 (`unauthorized`) is currently never toasted (temporary — see
 *   `SUPPRESS_UNAUTHORIZED_TOASTS`). When re-enabled it is throttled
 *   *globally* (one toast at a time,
 *   regardless of which query/mutation triggered it) because a real session
 *   expiry typically fails many concurrent requests at once.
 * - Generic backend errors (403, 5xx, network) are throttled per endpoint —
 *   derived from the first element of the query/mutation key — so a backend
 *   fault that fails many requests to the same endpoint (including a polling
 *   query with varying parameters) surfaces at most one toast per endpoint per
 *   `toastThrottleMs`. Different endpoints still fail independently.
 * - All other error kinds (validation, bad-request, not-found, conflict,
 *   rate-limited, unknown) are never throttled: they are actionable or
 *   input-specific, so the user must be told about each one.
 * - Mutations without a `mutationKey` are not throttled — they are
 *   user-initiated, and collapsing them into one shared bucket would let one
 *   failure hide an unrelated one.
 */
export function createAppQueryClient(
  options: CreateAppQueryClientOptions,
): QueryClient {
  const { notify } = options;
  const throttleMs = options.toastThrottleMs ?? DEFAULT_TOAST_THROTTLE_MS;
  const lastShownAtByKey = new Map<string, number>();
  let lastUnauthorizedToastAt = 0;

  const pruneThrottleMap = (now: number) => {
    if (lastShownAtByKey.size < MAX_THROTTLE_ENTRIES) return;
    for (const [key, shownAt] of lastShownAtByKey) {
      if (now - shownAt >= throttleMs) lastShownAtByKey.delete(key);
    }
    // Still full of fresh entries: evict oldest-first (Map iteration order is
    // insertion order).
    while (lastShownAtByKey.size >= MAX_THROTTLE_ENTRIES) {
      const oldest = lastShownAtByKey.keys().next().value;
      if (oldest === undefined) break;
      lastShownAtByKey.delete(oldest);
    }
  };

  const notifyError = (
    error: unknown,
    key: readonly unknown[] | undefined,
    meta: { suppressErrorToast?: boolean } | undefined,
  ) => {
    if (meta?.suppressErrorToast) return;

    const classified = classifyApiError(error);

    // The service-availability circuit breaker already surfaces these (and
    // consumers deliberately swallow ServiceUnavailableError) — a toast on
    // top would double-report.
    if (classified.kind === 'service-unavailable') return;

    // A session/transport failure behind a websocket RPC is owned by the
    // reconnect / grace-period flow, which shows its own "Checking
    // connection…" / logout UX. Without this, every polling query that rides
    // the same dead session would repeat an identical generic toast.
    if (classified.kind === 'session') return;

    // TEMPORARY: a backend issue returns spurious 401s at session start, so
    // 401 toasts are suppressed entirely for now. Remove this guard (the
    // global throttle below will take over again) once the backend is fixed.
    if (SUPPRESS_UNAUTHORIZED_TOASTS && classified.kind === 'unauthorized') {
      return;
    }

    const now = Date.now();

    if (classified.kind === 'unauthorized') {
      // Single-flight: a session expiry can fail many concurrent
      // queries/mutations at once. Show exactly one toast, not one per
      // failing request — even across different endpoints.
      if (now - lastUnauthorizedToastAt < throttleMs) return;
      lastUnauthorizedToastAt = now;
    } else if (GENERIC_ERROR_KINDS.has(classified.kind)) {
      // Generic backend fault: throttle to once per endpoint per window. A
      // keyless mutation skips throttling (it's user-initiated).
      const endpoint = endpointFromKey(key);
      if (endpoint !== undefined) {
        const throttleKey = `${endpoint}:${classified.kind}`;
        const lastShownAt = lastShownAtByKey.get(throttleKey) ?? 0;
        if (now - lastShownAt < throttleMs) return;
        pruneThrottleMap(now);
        lastShownAtByKey.set(throttleKey, now);
      }
    }
    // Non-generic kinds (validation, bad-request, not-found, conflict,
    // rate-limited, unknown) fall through and always toast — each is
    // actionable and needs the user's attention.

    notify(getErrorToastContent(classified));
  };

  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        notifyError(error, query.queryKey, query.meta);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _onMutateResult, mutation) => {
        if (mutation.meta?.suppressErrorToast) return;
        // A mutation whose `useMutation` options define `onError` is assumed
        // to already surface feedback — don't toast on top of it.
        if (mutation.options.onError) return;

        // TanStack calls this cache-level handler *before* observer-level
        // `mutate(variables, { onError })` callbacks, which the Mutation
        // object does not expose. Defer one macrotask so those local
        // callbacks can mark the error as handled (see
        // `markErrorHandledLocally`) before deciding to toast.
        setTimeout(() => {
          if (wasErrorHandledLocally(error)) return;

          notifyError(error, mutation.options.mutationKey, mutation.meta);
        }, 0);
      },
    }),
    defaultOptions: {
      ...options.defaultOptions,
      queries: {
        retry: shouldRetry,
        ...options.defaultOptions?.queries,
      },
      mutations: {
        ...options.defaultOptions?.mutations,
      },
    },
  });
}
